import React, { ReactNode } from 'react';
import moment from 'moment';

type WeekHeroProps = {
  weekStart: string;
  onPrev: () => void;
  onNext: () => void;
  onThisWeek: () => void;
  isCurrentWeek: boolean;
  actions?: ReactNode;
  sticky?: boolean;
};

const tokens = {
  blue: '#3b82c4',
  ink: '#32325d',
  inkSoft: '#525f7f',
  inkMute: '#8898aa',
  line: '#e9ecef',
};

const arrowStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 10,
  border: `1px solid ${tokens.line}`,
  background: '#fff',
  color: tokens.inkSoft,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

const WeekHero = ({ weekStart, onPrev, onNext, onThisWeek, isCurrentWeek, actions, sticky = true }: WeekHeroProps) => {
  const start = moment(weekStart);
  const end = moment(weekStart).endOf('isoWeek');
  const rangeLabel = `${start.format('MMM DD')} – ${end.format('MMM DD, YYYY')}`;

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 18,
    flexWrap: 'wrap',
    padding: sticky ? '14px 22px' : '22px 26px',
    borderRadius: 12,
    background: '#fff',
    color: tokens.ink,
    border: `1px solid ${tokens.line}`,
    ...(sticky ? { position: 'sticky', top: 0, zIndex: 5, boxShadow: '0 2px 5px rgba(50,50,93,.06)' } : {}),
  };

  return (
    <div style={containerStyle}>
      <div className="d-flex align-items-center" style={{ gap: 14 }}>
        <button type="button" aria-label="Previous week" title="Previous week" style={arrowStyle} onClick={onPrev}>
          <i className="fas fa-chevron-left" />
        </button>
        <div>
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '.09em',
              color: tokens.inkMute,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <i className="fas fa-calendar-alt" />
            Pay Period
          </div>
          <div aria-live="polite" style={{ fontSize: sticky ? 19 : 23, fontWeight: 800, lineHeight: 1.15 }}>
            {rangeLabel}
          </div>
        </div>
        <button type="button" aria-label="Next week" title="Next week" style={arrowStyle} onClick={onNext}>
          <i className="fas fa-chevron-right" />
        </button>
        <button
          type="button"
          onClick={onThisWeek}
          disabled={isCurrentWeek}
          style={{
            borderRadius: 20,
            padding: '7px 14px',
            border: `1px solid ${tokens.blue}`,
            background: isCurrentWeek ? tokens.blue : 'transparent',
            color: isCurrentWeek ? '#fff' : tokens.blue,
            cursor: isCurrentWeek ? 'default' : 'pointer',
            fontSize: 13,
          }}
        >
          This week
        </button>
      </div>
      {actions && (
        <div className="d-flex align-items-center ml-auto" style={{ gap: 10, flexWrap: 'wrap' }}>
          {actions}
        </div>
      )}
    </div>
  );
};

export default WeekHero;
